import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonRefresherContent,
  IonGrid,
  IonModal,
  useIonRouter
} from '@ionic/react';
import { createOutline, mailOutline, ellipsisVertical, chevronBack, closeOutline } from 'ionicons/icons';
import { useLocation, useParams } from 'react-router-dom';
import { getZohoEstimate, getZohoInvoice, isInvoiceAchPending } from '../services/zohoInvoicingService';
import type { ZohoInvoice } from '../services/zohoInvoicingService';
import { getInvoiceMetadata } from '../services/invoiceMetadataService';
import type { InvoiceMetadata } from '../services/invoiceMetadataService';
import BasePageWithHeader, { TitleCard} from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import CreateInvoice from './CreateInvoice';
import EmailCompose from './EmailCompose';
import styles from './Invoice.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';

interface InvoiceParams extends Record<string, string | undefined> {
  invoiceId?: string;
  estimateId?: string;
}

interface InvoiceProps {
  onClose?: () => void;
  invoiceId?: number | string;
  initialData?: ZohoInvoice;
  documentType?: 'invoice' | 'estimate';
  /** The caller is still resolving the invoice ID — show the loading screen instead of a "no ID" error. */
  resolvingId?: boolean;
}

const BUSINESS_FALLBACK = {
  name:    'R&S Maids Service',
  address: '22002 64th Avenue West, STE M9, Mountlake Terrace, WA 98043',
  phone:   '(425) 866-3424',
  email:   'support@rsmaidsservice.net',
  website: 'https://rsmaidsservice.com',
};

const Invoice: React.FC<InvoiceProps> = ({ onClose, invoiceId: invoiceIdProp, initialData, documentType = 'invoice', resolvingId = false }) => {
  const { t: tr } = useTranslation();
  const { invoiceId: invoiceIdParam, estimateId: estimateIdParam } = useParams<InvoiceParams>();
  const isEstimate = documentType === 'estimate';
  const location = useLocation();
  const resolvedRouteId = isEstimate ? estimateIdParam : invoiceIdParam;
  const invoiceId = invoiceIdProp ?? resolvedRouteId ?? invoiceIdParam ?? estimateIdParam;
  const router = useIonRouter();
  const [invoice, setInvoice] = useState<ZohoInvoice | null>(initialData ?? null);
  const [metadata, setMetadata] = useState<InvoiceMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);

  // Refs for PositionedRefresher
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleRow = (i: number) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(i)) { next.delete(i); } else { next.add(i); }
    return next;
  });

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      const isDesktopRoute = location.pathname.startsWith('/desktop/');
      const fallbackPath = isEstimate
        ? (isDesktopRoute ? '/desktop/tabs/estimates' : '/tabs/estimates')
        : '/tabs/bookings';
      router.push(fallbackPath);
    }
  }, [isEstimate, location.pathname, onClose, router]);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  const loadInvoice = useCallback(async (background = false) => {
    if (!invoiceId && resolvingId) return;
    if (!invoiceId) {
      setError(isEstimate ? tr('pages.Invoice.noEstimateIdProvided') : tr('pages.Invoice.noInvoiceIdProvided'));
      setIsLoading(false);
      return;
    }
    try {
      if (!background) setIsLoading(true);
      setError(null);
      const [data, meta] = await Promise.all([
        isEstimate ? getZohoEstimate(String(invoiceId)) : getZohoInvoice(String(invoiceId)),
        isEstimate ? Promise.resolve(null) : getInvoiceMetadata(invoiceId),
      ]);
      setInvoice(data);
      setMetadata(meta);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : tr('pages.Invoice.failedToLoad', { v1: translateDynamic(isEstimate ? 'estimate' : 'invoice') }));
    } finally {
      if (!background) setIsLoading(false);
    }
  }, [invoiceId, isEstimate, resolvingId, tr]);

  useEffect(() => { Promise.resolve().then(() => loadInvoice(!!initialData)); }, [invoiceId, initialData, loadInvoice]);

  if (isLoading) {
    const loadingContent = (
      <IonContent className={styles.loadingContent}>
        <div className={styles.loadingContainer}>
          <IonSpinner name="bubbles" color="primary" />
          <IonText>{tr('pages.Invoice.loading')} {translateDynamic(isEstimate ? 'estimate' : 'invoice')}...</IonText>
        </div>
      </IonContent>
    );
    return onClose
      ? <div className={styles.modalWrapper}>{loadingContent}</div>
      : <IonPage className={styles.page}>{loadingContent}</IonPage>;
  }

  if (error || !invoice) {
    const errorContent = (
      <IonContent className={styles.errorContent}>
        <div className={styles.errorContainer}>
          <IonText color="danger">
            <h2>{tr('pages.Invoice.errorLoading')} {isEstimate ? tr('pages.Invoice.estimate') : tr('pages.Invoice.invoice')}</h2>
            <p>{error || tr('pages.Invoice.noDataAvailable', { v1: translateDynamic(isEstimate ? 'estimate' : 'invoice') })}</p>
          </IonText>
          <IonButton fill="outline" onClick={handleClose}>
            {isEstimate ? tr('pages.Invoice.backToEstimates') : tr('pages.Invoice.backToBookings')}
          </IonButton>
        </div>
      </IonContent>
    );
    return onClose
      ? <div className={styles.modalWrapper}>{errorContent}</div>
      : <IonPage className={styles.page}>{errorContent}</IonPage>;
  }

  const statusStyleMap: Record<string, string> = {
    paid:            styles.statusPaid,
    overdue:         styles.statusOverdue,
    sent:            styles.statusSent,
    viewed:          styles.statusViewed,
    unpaid:          styles.statusUnpaid,
    draft:           styles.statusDraft,
    void:            styles.statusVoid,
    partially_paid:  styles.statusPartial,
  };
  const statusClass = statusStyleMap[invoice.status] ?? styles.statusDraft;
  const ba = invoice.billing_address;
  const invoiceRecord = invoice as unknown as Record<string, unknown>;
  const rawBillingAddress = invoiceRecord['billing_address'];
  const billingAddressObject = (
    ba
    ?? (typeof rawBillingAddress === 'object' && rawBillingAddress !== null
      ? rawBillingAddress as {
          address?: string;
          street?: string;
          street2?: string;
          attention?: string;
          city?: string;
          state?: string;
          state_code?: string;
          zip?: string;
          country?: string;
          phone?: string;
        }
      : undefined)
  );
  const fallbackAddressText = [
    invoiceRecord['customer_default_address'],
    invoiceRecord['billing_address_formatted'],
  ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);

  const billingAddressLines = [
    billingAddressObject?.attention,
    billingAddressObject?.address ?? billingAddressObject?.street,
    billingAddressObject?.street2,
    [billingAddressObject?.city, billingAddressObject?.state ?? billingAddressObject?.state_code, billingAddressObject?.zip].filter(Boolean).join(', ') || undefined,
    billingAddressObject?.country,
    billingAddressObject?.phone,
  ].filter((line): line is string => !!line && line.trim().length > 0);

  if (billingAddressLines.length === 0 && fallbackAddressText) {
    billingAddressLines.push(
      ...fallbackAddressText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    );
  }
  const fmtDate = (d?: string) => {
    if (!d || d.startsWith('0000')) return '—';
    // Append T00:00:00 for date-only strings to force local-time parsing
    const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    return dt.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const subtotal = invoice.sub_total ?? invoice.line_items?.reduce((s, l) => s + l.quantity * l.rate, 0) ?? invoice.total ?? 0;

  // Build map: tax base name -> tax_percentage from line_item_taxes
  // e.g. "City (4.05%)" -> { "City": 4.05 }, matching top-level taxes[].tax_name
  const taxPercentageMap: Record<string, number> = {};
  (invoice.line_items ?? []).forEach(l => {
    (l.line_item_taxes ?? []).forEach(t => {
      // Strip optional " (X%)"/" (X.XX%)" suffix to get base name
      const baseName = t.tax_name.replace(/\s*\([\d.]+%\)\s*$/, '').trim();
      if (baseName && t.tax_percentage != null) {
        taxPercentageMap[baseName] = t.tax_percentage;
      }
      // Also store the full name as key in case taxes[] uses it
      if (t.tax_name && t.tax_percentage != null) {
        taxPercentageMap[t.tax_name] = t.tax_percentage;
      }
    });
  });
  const totalAmount = invoice.total ?? 0;
  const amountPaid = invoice.payment_made ?? (totalAmount - (invoice.balance ?? 0));
  const balanceDue = invoice.balance ?? 0;
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const estimateDoc = invoice as ZohoInvoice & { estimate_number?: string; expiry_date?: string };

  const invoiceNumber = isEstimate ? (estimateDoc.estimate_number ?? invoice.invoice_number) : invoice.invoice_number;
  const invoiceDate = fmtDate(invoice.date);

  // Due date/expiry date based on document type
  const dueDate = isEstimate
    ? (estimateDoc.expiry_date ? fmtDate(estimateDoc.expiry_date) : (invoice.due_date ? fmtDate(invoice.due_date) : '—'))
    : (metadata?.dueDate
      ? fmtDate(metadata.dueDate)
      : invoice.due_date
        ? fmtDate(invoice.due_date)
        : '—');

  // Helper: look up a custom field by label (case-insensitive)
  const getCustomField = (label: string) =>
    invoice.custom_fields?.find(f => f.label.toLowerCase() === label.toLowerCase())?.value ?? null;

  // Service month: prefer Zoho custom field, fall back to Firestore metadata
  const serviceMonthRaw = getCustomField('service month') ?? metadata?.serviceMonth ?? null;
  const serviceMonthDisplay = serviceMonthRaw
    ? (() => {
        if (/^\d{4}-\d{2}$/.test(serviceMonthRaw)) {
          const [y, m] = serviceMonthRaw.split('-').map(Number);
          return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
        }
        return serviceMonthRaw;
      })()
    : '—';

  // Payment method: prefer Zoho custom field, fall back to Firestore metadata (kept for reference, not shown on invoice)
  // const paymentMethod = getCustomField('payment method') ?? metadata?.paymentMethod ?? '—';

  const pageContent = (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={isEstimate ? tr('pages.Invoice.estimate') : tr('pages.Invoice.invoice')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        onClose ? (
          <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        ) : (
          <IonButton shape='round' className={styles.backButton} onClick={handleClose}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        )
      }
      endButtons={
        <>
          <IonButton shape="round" className={styles.headerIconButton} onClick={() => setShowEdit(true)}>
            <IonIcon icon={createOutline} />
          </IonButton>
          {!isEstimate && (
            <IonButton shape="round" className={styles.headerIconButton} onClick={() => setShowEmail(true)}>
              <IonIcon icon={mailOutline} />
            </IonButton>
          )}
          <IonButton shape="round" className={styles.headerIconButton}>
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        </>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <PositionedRefresher
          titleRef={titleCardElementRef}
          contentRef={contentElementRef}
          offset={0}
          anchor="bottom"
          id="invoice-refresher"
          pullMin={120}
          pullMax={200}
          pullFactor={0.6}
          disabled={refresherDisabled}
          onIonRefresh={(e) => {
            const doRefresh = async () => {
              try {
                await loadInvoice();
              } finally {
                e.detail.complete();
              }
            };
            doRefresh();
          }}
          style={{
            '--ion-color-step-250': 'var(--ion-background-color)',
            '--ion-color-step-200': 'var(--ion-background-color)',
            '--ion-background-color': 'transparent',
          } as React.CSSProperties}
        >
          <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
        </PositionedRefresher>
        <IonGrid
          ref={contentElementRef}
          className={styles.invoiceContainer}
          onScroll={(e) => {
            const top = (e.target as HTMLElement).scrollTop || 0;
            const atTop = top <= 5;
            if (atTop !== wasAtTop.current) {
              wasAtTop.current = atTop;
              setRefresherDisabled(!atTop);
            }
            handleScroll(top);
          }}
        >
          <div ref={titleCardElementRef}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={isEstimate ? tr('pages.Invoice.estimate') : tr('pages.Invoice.invoice')}
              cardClassName={styles.invoiceTitleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.invoicePageTitle}
            >
              <div className={styles.invoiceHeader}>
                <div className={styles.companySection}>
                  <div className={styles.companyLogoWrapper}>
                    <img
                      src="/assets/icon/maskable_icon_192.png"
                      alt={tr('pages.Invoice.rSMaidsService')}
                      className={styles.companyLogoImg}
                    />
                  </div>
                  <h1 className={styles.companyName}>{invoice.company_name ?? BUSINESS_FALLBACK.name}</h1>
                </div>

                <div className={styles.topSection}>
                  <div className={styles.invoiceDetailsColumn}>
                    <div className={styles.statusRow}>
                      <span className={styles.invoiceNumber}>{invoiceNumber}</span>
                      {isInvoiceAchPending(invoice) ? (
                        <span className={styles.statusPending}>{tr('pages.Invoice.achPending')}</span>
                      ) : (
                        <span className={statusClass}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      )}
                    </div>

                    <h3 className={styles.columnTitle}>{isEstimate ? tr('pages.Invoice.estimateDetails') : tr('pages.Invoice.invoiceDetails')}</h3>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{isEstimate ? tr('pages.Invoice.estimateDate') : tr('pages.Invoice.invoiceDate')}</span>
                      <span className={styles.detailValue}>{invoiceDate}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{isEstimate ? tr('pages.Invoice.expiryDate') : tr('pages.Invoice.dueDate')}</span>
                      <span className={styles.detailValue}>{dueDate}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{tr('pages.Invoice.serviceMonth')}</span>
                      <span className={styles.detailValue}>{serviceMonthDisplay}</span>
                    </div>
                  </div>

                  <div className={styles.accountColumn}>
                    <h3 className={styles.columnTitle}>{tr('pages.Invoice.billTo')}</h3>
                    <p className={styles.billingName}>{invoice.customer_name ?? '\u2014'}</p>
                    <div className={styles.billingAddress}>
                      {billingAddressLines.map((line, index) => (
                        <p key={`${line}-${index}`}>{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TitleCard>
          </div>

          {/* Items Table */}
          <section className={styles.itemsSection}>
            <table className={styles.itemsTable}>
              <thead>
                <tr>
                  <th className={styles.itemHeader}>{tr('pages.Invoice.item')}</th>
                  <th className={styles.qtyHeader}>{tr('pages.Invoice.qty')}</th>
                  <th className={styles.priceHeader}>{tr('pages.Invoice.rate')}</th>
                  <th className={styles.taxHeader}>{tr('pages.Invoice.tax')}</th>
                  <th className={styles.totalHeader}>{tr('pages.Invoice.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items ?? []).map((l, i) => {
                  const lineTax = (l.line_item_taxes ?? []).reduce((s, t) => s + t.tax_amount, 0);
                  const isExpanded = expandedRows.has(i);
                  const hasDesc = !!(l.description && l.name);
                  return (
                    <tr key={i} className={styles.itemRow}>
                      <td className={styles.itemCell}>
                        <div className={styles.itemName}>{l.name ?? l.description}</div>
                        {hasDesc && (
                          <>
                            <div className={`${styles.itemDescription} ${isExpanded ? styles.itemDescExpanded : ''}`}>
                              {l.description}
                            </div>
                            <button className={styles.expandBtn} onClick={() => toggleRow(i)}>
                              {isExpanded ? tr('pages.Invoice.showLess') : tr('pages.Invoice.showMore')}
                            </button>
                          </>
                        )}
                      </td>
                      <td className={styles.qtyCell}>{l.quantity}</td>
                      <td className={styles.priceCell}>{fmt(l.rate)}</td>
                      <td className={styles.taxCell}>{lineTax > 0 ? fmt(lineTax) : '—'}</td>
                      <td className={styles.totalCell}>{fmt(l.quantity * l.rate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {/* Comments Section */}
          {invoice.notes && (
            <section className={styles.commentsSection}>
              <h3 className={styles.commentsTitle}>{tr('pages.Invoice.comments')}</h3>
              <div className={styles.commentsText}>
                {invoice.notes}
              </div>
            </section>
          )}

          {/* Totals Section */}
          <section className={styles.totalsSection}>
            <div className={styles.totalsGrid}>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>{tr('pages.Invoice.subtotal')}{invoice.is_inclusive_tax ? tr('pages.Invoice.taxInclusive') : ''}</span>
                <span className={styles.totalValue}>{fmt(subtotal)}</span>
              </div>
              {!!invoice.discount_total && invoice.discount_total > 0 && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{tr('pages.Invoice.discount')}</span>
                  <span className={styles.totalValue}>-{fmt(invoice.discount_total)}</span>
                </div>
              )}
              {(invoice.taxes ?? []).map((tax, i) => (
                <div key={i} className={styles.totalRow}>
                  <span className={styles.totalLabel}>
                    {tax.tax_name}{taxPercentageMap[tax.tax_name] != null ? ` (${taxPercentageMap[tax.tax_name]}%)` : ''}
                  </span>
                  <span className={styles.totalValue}>{tax.tax_amount_formatted ?? fmt(tax.tax_amount)}</span>
                </div>
              ))}
              {!!invoice.shipping_charge && invoice.shipping_charge > 0 && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{tr('pages.Invoice.shippingCharge')}</span>
                  <span className={styles.totalValue}>{fmt(invoice.shipping_charge)}</span>
                </div>
              )}
              {!!invoice.adjustment && invoice.adjustment !== 0 && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{invoice.adjustment_description || tr('pages.Invoice.adjustment')}</span>
                  <span className={styles.totalValue}>{fmt(invoice.adjustment)}</span>
                </div>
              )}
              <div className={styles.totalRowFinal}>
                <span className={styles.totalLabelFinal}>{tr('pages.Invoice.total')}</span>
                <span className={styles.totalValueFinal}>{fmt(totalAmount)}</span>
              </div>
              <div className={styles.amountPaidRow}>
                <span className={styles.amountPaidLabel}>{tr('pages.Invoice.paymentMade')}</span>
                <span className={styles.amountPaidValue}>-{fmt(amountPaid)}</span>
              </div>
              {!!invoice.write_off_amount && invoice.write_off_amount > 0 && (
                <div className={styles.amountPaidRow}>
                  <span className={styles.amountPaidLabel}>{tr('pages.Invoice.writeOff')}</span>
                  <span className={styles.amountPaidValue}>-{fmt(invoice.write_off_amount)}</span>
                </div>
              )}
              <div className={styles.totalRowFinal}>
                <span className={`${styles.totalLabelFinal} ${styles.blue}`}>{isEstimate ? tr('pages.Invoice.balance') : tr('pages.Invoice.balanceDue')}</span>
                <span className={`${styles.totalValueFinal} ${styles.blue}`}>{fmt(balanceDue)}</span>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className={styles.footer}>
            <p className={styles.footerTitle}>{tr('pages.Invoice.thankYouForYourBusiness')}</p>
            {invoice.terms && (
              <div className={styles.termsSection}>
                <p className={styles.termsTitle}>{tr('pages.Invoice.termsConditions')}</p>
                <p className={styles.footerText}>{invoice.terms}</p>
              </div>
            )}
            <p className={styles.footerContact}>{invoice.company_name ?? BUSINESS_FALLBACK.name} &bull; {(() => { const a = invoice.company_address; return a ? [a.address, a.city, a.state && a.zip ? `${a.state} ${a.zip}` : (a.state ?? a.zip)].filter(Boolean).join(', ') : BUSINESS_FALLBACK.address; })()}</p>
            <p className={styles.footerContact}>{tr('pages.Invoice.phone')} {invoice.phone ?? BUSINESS_FALLBACK.phone} &bull; {BUSINESS_FALLBACK.email}</p>
            <p className={styles.footerLink}>{invoice.website ?? BUSINESS_FALLBACK.website}</p>
          </footer>
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
  );

  const withModals = (
    <>
      {pageContent}

      <IonModal
        isOpen={showEdit}
        onDidDismiss={() => { setShowEdit(false); loadInvoice(true); }}
        onWillDismiss={() => { setShowEdit(false); loadInvoice(true); }}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <CreateInvoice
          edit={{ invoice, isEstimate, metadata: metadata ? { serviceMonth: metadata.serviceMonth } : undefined }}
          onClose={() => { setShowEdit(false); loadInvoice(true); }}
        />
      </IonModal>

      <IonModal
        isOpen={showEmail && !isEstimate}
        onDidDismiss={() => setShowEmail(false)}
        onWillDismiss={() => setShowEmail(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <EmailCompose
          entityType="invoice"
          entityId={invoice.invoice_id}
          entityNumber={invoice.invoice_number}
          defaultTo={invoice.email ?? invoice.customer_email}
          onClose={() => setShowEmail(false)}
        />
      </IonModal>
    </>
  );

  return onClose
    ? <div className={styles.modalWrapper}>{withModals}</div>
    : <IonPage className={styles.page}>{withModals}</IonPage>;
};

export default Invoice;
